/**
 * CR001: Spend & Earn Offers Types
 * Separate offering for spend-to-earn promotions (gift card/cash/points rewards)
 */

import { formatEligibilityRange } from "@/lib/deals/formatEligibilityRange";

// Enums matching database types
export type SpendEarnCategory = 'REBATE' | 'DISCOUNT' | 'POINTS' | 'BONUS' | 'UNCLASSIFIED';
export type RewardForm = 'GIFT_CARD' | 'CASH' | 'POINTS' | 'OTHER';
export type QualifyingAction = 'SPEND' | 'PURCHASE' | 'BOOK' | 'APPLY' | 'SUBSCRIBE' | 'OTHER';
export type SpendEarnAudience = 'corporate_bulk' | 'personal' | 'unknown';

export interface SpendEarnOffer {
  id: string;
  
  // Core fields
  merchant_id: string;
  merchant_name: string;
  source_url: string;
  headline_mechanic: string;
  offer_category: SpendEarnCategory;
  reward_form: RewardForm;
  
  // Qualifying action
  qualifying_action: QualifyingAction | null;
  
  // Value fields
  qualifying_spend_cad: number | null;
  discount_percent: number | null;
  discount_amount_cad: number | null;
  points_amount: number | null;
  points_program_name: string | null;
  max_reward_cad: number | null;
  computed_value_cad: number | null;
  effective_percent_back: number | null;
  
  // Audience & constraints
  audience_hint: SpendEarnAudience | null;
  membership_required: string | null;
  product_scope: string | null;
  exclusions: string | null;
  fine_print: string | null;
  
  // Dates
  valid_from: string | null;
  valid_until: string | null;
  claim_window_end: string | null;
  
  // Links
  deal_link: string | null;
  detail_link: string | null;
  claim_url: string | null;
  view_offer_url: string | null;
  terms_url: string | null;
  
  // Raw text
  raw_offer_text: string | null;
  raw_source_notes: string | null;
  
  // Scoring
  confidence: number | null;
  normalized_value_score: number | null;
  
  // Run tracking
  run_id: string | null;
  run_date: string | null;
  
  // Timestamps
  created_at: string;
  updated_at: string;
}

// Display helpers
export function getRewardFormLabel(form: RewardForm): string {
  switch (form) {
    case 'GIFT_CARD': return 'Gift Card';
    case 'CASH': return 'Cash';
    case 'POINTS': return 'Points';
    case 'OTHER': return 'Reward';
  }
}

export function getCategoryLabel(category: SpendEarnCategory): string {
  switch (category) {
    case 'REBATE': return 'Rebate';
    case 'DISCOUNT': return 'Discount';
    case 'POINTS': return 'Points';
    case 'BONUS': return 'Bonus';
    case 'UNCLASSIFIED': return 'Offer';
  }
}

export function getQualifyingActionLabel(action: QualifyingAction | null): string {
  if (!action) return 'Spend';
  switch (action) {
    case 'SPEND': return 'Spend';
    case 'PURCHASE': return 'Purchase';
    case 'BOOK': return 'Book';
    case 'APPLY': return 'Apply';
    case 'SUBSCRIBE': return 'Subscribe';
    case 'OTHER': return 'Qualify';
  }
}

export function computeOfferStatus(offer: SpendEarnOffer): 'active' | 'upcoming' | 'expired' {
  const now = new Date();
  const validFrom = offer.valid_from ? new Date(offer.valid_from) : null;
  const validUntil = offer.valid_until ? new Date(offer.valid_until) : null;
  
  if (validUntil && validUntil < now) return 'expired';
  if (validFrom && validFrom > now) return 'upcoming';
  return 'active';
}

/**
 * Format offer validity with user-proof date formatting
 * CR282: Uses formatEligibilityRange for consistent formatting (no Jan 28–3 compression)
 */
export function formatOfferValidity(offer: SpendEarnOffer): string {
  const parts: string[] = [];
  
  // Use eligibility range formatter for main validity
  const validityRange = formatEligibilityRange(offer.valid_from, offer.valid_until);
  if (validityRange) {
    parts.push(validityRange);
  }
  
  // Claim window is separate from main validity
  if (offer.claim_window_end) {
    const claim = new Date(offer.claim_window_end);
    parts.push(`Claim by ${claim.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`);
  }
  
  return parts.join(' • ') || 'No dates specified';
}
